/**
 * The product catalogue for the company currently open.
 *
 * The second screen built entirely from the design system, and the first with a
 * JOIN behind it. `Product` stores two foreign keys, so every row resolves its
 * category name and tax rate; that resolution lives in `productReader` and is
 * scoped to the business, so a product can never be shown beside another
 * company's category or priced at their tax rate.
 *
 * The rate column is the reason `formatRateBp` exists and is used here rather
 * than a local `rate / 100 + '%'`. A tax rate displayed as 0.18% instead of 18%
 * is the kind of defect that survives review because it still looks like a number.
 */
import { useState, useEffect } from 'react';
import { DataTable } from '@ui/components/DataTable';
import { StatusBadge } from '@ui/components/StatusBadge';
import { formatMoney, formatRateBp } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { useProductFilters, useProducts } from './useProducts';
import { useDeactivateProduct } from './useProductWrites';
import { useActiveBusiness } from '@features/documents/useDocuments';
import { STANDARD_GST_RATES } from '@features/masterData/gstRates';
import { ProductEditorDrawer } from './ProductEditorDrawer';
import { Button } from '@ui/components/Button';
import { ConfirmDialog } from '@ui/components/Modal';
import type { Column } from '@ui/lib/table';
import type { ProductListItem } from '@data/services/productReader';
import { getDb } from '@data/db/database';

const KINDS = [
  { value: 'ALL', label: 'Everything' },
  { value: 'GOODS', label: 'Goods' },
  { value: 'SERVICE', label: 'Services' },
] as const;

/** A blank cell marker. A missing value is information, not a rendering error. */
function Missing() {
  return <span className="text-slate-600">—</span>;
}

function columnsFor(
  onEdit: (r: ProductListItem) => void,
  onDeactivate: (r: ProductListItem) => void,
): Column<ProductListItem>[] {
  return [
    { key: 'name', header: 'Item', value: (r) => r.product.name },
    { key: 'sku', header: 'SKU', value: (r) => r.product.sku },
    {
      key: 'category',
      header: 'Category',
      // Sorts on the RESOLVED NAME, not on the id — a user sorting by category
      // expects alphabetical, and an id sort is meaningless to them.
      value: (r) => r.categoryName,
      render: (r) => r.categoryName ?? <Missing />,
    },
    { key: 'hsn', header: 'HSN/SAC', value: (r) => r.product.hsnSac },
    { key: 'unit', header: 'Unit', value: (r) => r.product.unit },
    {
      key: 'tax',
      header: 'GST',
      value: (r) => r.taxRateBp,
      // formatRateBp is core's, and the ONLY sanctioned way to turn 1800 into
      // "18". It already refuses to invent precision the store does not have.
      render: (r) => (r.taxRateBp === null ? <Missing /> : <>{formatRateBp(r.taxRateBp)}%</>),
    },
    {
      key: 'price',
      header: 'Sale price',
      align: 'right',
      value: (r) => r.product.salePricePaise,
      // Money through core, never toFixed: the ESLint ban is not the point, the
      // consistency is. This column and the invoice total must read alike.
      render: (r) => formatMoney(asMoney(r.product.salePricePaise)),
    },
    {
      key: 'stock',
      header: 'Tracked',
      value: (r) => (r.product.isStockTracked ? 'STOCKED' : 'NOT STOCKED'),
      render: (r) => <StatusBadge status={r.product.isStockTracked ? 'STOCKED' : 'NOT STOCKED'} />,
    },
    {
      key: 'active',
      header: 'Status',
      value: (r) => (r.product.isActive ? 'ACTIVE' : 'INACTIVE'),
      render: (r) => (
        <StatusBadge status={r.product.isActive ? 'ACTIVE' : 'INACTIVE'} />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      // The column holds buttons, so its searchable `value` is empty on purpose —
      // `value` is what the table's own filter searches over.
      value: () => '',
      render: (r) => (
        <span className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="edit-product"
            className="text-brand-400 underline"
            onClick={() => onEdit(r)}
          >
            Edit
          </button>
          {r.product.isActive && (
            <button
              type="button"
              data-testid="deactivate-product"
              className="text-red-300 underline"
              onClick={() => onDeactivate(r)}
            >
              Deactivate
            </button>
          )}
        </span>
      ),
    },
  ];
}

export function ProductScreen() {
  const [kind, setKind] = useState<'ALL' | 'GOODS' | 'SERVICE'>('ALL');
  const [categoryId, setCategoryId] = useState<string>('');
  // No search state here — `DataTable` owns table filtering and renders its own
  // search box. A second one above the table meant two search fields on every
  // products screen, and on a phone they pushed the table itself down the page.
  // The two were not equivalent either: this one matched name, code and HSN,
  // while the table's matches every column the user can actually see.
  //
  // `null` closed; `'new'` creating; a row editing. One state, so a blank create
  // form and a populated edit form can never both be open.
  const [editing, setEditing] = useState<ProductListItem['product'] | 'new' | null>(null);
  const [deactivating, setDeactivating] = useState<ProductListItem | null>(null);
  const activeBusiness = useActiveBusiness();
  const businessId = activeBusiness.data;
  const deactivate = useDeactivateProduct();
  const products = useProducts({
    kind: kind === 'ALL' ? undefined : kind,
    categoryId: categoryId === '' ? undefined : categoryId,
  });
  const filters = useProductFilters();
  const rows = products.data ?? [];

  // Ensure standard GST tax rates exist for this business (0%, 5%, 12%, 18%, 28%).
  // They are created on first visit to the Products screen if missing.
  //
  // businessId comes from useActiveBusiness(), NOT from taxRates[0], because an
  // empty taxRates array makes taxRates[0] undefined — which used to make this
  // effect bail out before creating ANY rates. That was a chicken-and-egg bug:
  // the rates were never created because they did not exist yet, so the dropdown
  // stayed empty forever.
  useEffect(() => {
    if (!businessId) return;
    if (!filters.data?.taxRates) return;
    const existingRateBps = new Set(filters.data?.taxRates.map((t) => t.rateBp));
    const missing = STANDARD_GST_RATES.filter((r) => !existingRateBps.has(r.rateBp));
    if (missing.length === 0) return;

    const db = getDb();

    db.transaction('rw', db.taxRates, async () => {
      for (const rate of missing) {
        const id = `taxrate-${rate.rateBp}-${businessId}`;
        const existing = await db.taxRates.get(id);
        if (!existing) {
          await db.taxRates.add({
            id,
            businessId,
            name: rate.name,
            rateBp: rate.rateBp,
            cessBp: 0,
            isDefault: rate.isDefault,
            isActive: true,
          });
        }
      }
    })
      .then(() => void filters.refetch())
      .catch(() => {
        // Non-fatal: user can still use existing rates
      });
  }, [filters.data?.taxRates, businessId]);

  return (
    <div className="flex flex-col p-4 text-slate-100">

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {/* "Products", not "Items". The menu calls this screen Products, and a
            page whose heading disagrees with the link that opened it is the
            first sign to a user that they are somewhere they did not choose. */}
        <h1 className="text-lg font-semibold">Products</h1>
        <div role="group" aria-label="Filter by type" className="flex gap-1">
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => setKind(k.value)}
              aria-pressed={kind === k.value}
              className={
                kind === k.value
                  ? 'rounded bg-brand-500 px-3 py-1 text-sm text-white'
                  : 'rounded border border-slate-700 px-3 py-1 text-sm text-slate-300 hover:border-slate-500'
              }
            >
              {k.label}
            </button>
          ))}
        </div>

        <label className="ml-2 flex items-center gap-1 text-sm text-slate-400">
          Category
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.currentTarget.value)}
            aria-label="Filter by category"
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
          >
            <option value="">All</option>
            {(filters.data?.categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <Button data-testid="new-product" onClick={() => setEditing('new')}>
          Add item
        </Button>
      </div>

      {products.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <DataTable
          rows={rows}
          columns={columnsFor(
            (r) => setEditing(r.product),
            (r) => setDeactivating(r),
          )}
          rowKey={(r) => r.product.id}
          caption="items"
          emptyMessage={
            // The table owns the query, so the screen cannot tell a typed search
            // from an empty catalogue. The second message stays accurate either way.
            'No items yet. They arrive with an import, or you can add one.'
          }
        />
      )}

      <ProductEditorDrawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        product={editing === 'new' ? null : editing}
        taxRates={filters.data?.taxRates ?? []}
      />

      <ConfirmDialog
        open={deactivating !== null}
        title={`Deactivate ${deactivating?.product.name ?? ''}?`}
        message={
          'The item stays on every invoice it was ever sold on, at the price it was sold ' +
          'for. It simply stops being offered on new documents.'
        }
        confirmLabel="Deactivate"
        destructive
        busy={deactivate.isPending}
        onConfirm={() => {
          if (deactivating) deactivate.mutate(deactivating.product.id);
          setDeactivating(null);
        }}
        onCancel={() => setDeactivating(null)}
      />
    </div>
  );
}
